import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface CarouselProps<T> {
  items: T[];
  getKey: (item: T, index: number) => string;
  renderItem: (item: T, index: number) => React.ReactNode;
  label: string;
  tone?: 'light' | 'dark';
  /** Slide width per breakpoint. Mobile shows one item with a peek of the next. */
  itemClassName?: string;
  /** Optional per-item slide width (overrides itemClassName) for mixed-format sets. */
  getItemClassName?: (item: T, index: number) => string;
  /** Long sets get a counter + progress bar instead of dots. */
  indicator?: 'dots' | 'progress';
}
const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Lightweight native scroll-snap carousel: swipe on mobile, arrows on desktop.
 * Must sit inside a `px-5 sm:px-8` container — it bleeds to the screen edge.
 */
export function Carousel<T>({
  items,
  getKey,
  renderItem,
  label,
  tone = 'light',
  itemClassName = 'w-[82%] sm:w-[46%] lg:w-[31.5%]',
  getItemClassName,
  indicator
}: CarouselProps<T>) {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const dark = tone === 'dark';
  const mode = indicator ?? (items.length > 7 ? 'progress' : 'dots');

  /** Scroll offset of each slide relative to the first — supports mixed widths. */
  const offsetOf = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track || track.children.length === 0) return 0;
    const first = track.children[0] as HTMLElement;
    const target = track.children[index] as HTMLElement | undefined;
    return target ? target.offsetLeft - first.offsetLeft : 0;
  }, []);
  const nearestIndex = useCallback((scrollLeft: number) => {
    let best = 0;
    let bestDistance = Infinity;
    for (let i = 0; i < items.length; i += 1) {
      const distance = Math.abs(offsetOf(i) - scrollLeft);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    }
    return best;
  }, [items.length, offsetOf]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({
      left: 0
    });
    setActive(0);
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const last = items.length - 1;
        const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
        setActive(atEnd ? last : nearestIndex(track.scrollLeft));
      });
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => {
      track.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [items, nearestIndex]);
  const goTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(items.length - 1, index));
    track.scrollTo({
      left: offsetOf(clamped),
      behavior: 'smooth'
    });
  };
  const arrowClass = `flex h-11 w-11 items-center justify-center rounded-full border transition-colors duration-150 ease-out disabled:opacity-30 focus:outline-none focus-visible:ring-4 ${dark ? 'border-ivory/25 text-ivory hover:bg-ivory/10 focus-visible:ring-ember-light/40' : 'border-ink/20 text-ink hover:bg-ink/5 focus-visible:ring-ember/30'}`;
  return <div data-id="element-40" data-mp="eh8z5oxwncxs">
      <ul ref={trackRef} aria-label={label} className="no-scrollbar -mx-5 flex snap-x snap-mandatory scroll-px-5 gap-4 overflow-x-auto px-5 pb-2 sm:-mx-8 sm:scroll-px-8 sm:gap-5 sm:px-8" data-id="element-41" data-mp="ehipxeqrxo4j">
        {items.map((item, index) => <li key={getKey(item, index)} aria-label={`${index + 1} of ${items.length}`} className={`flex shrink-0 snap-start ${getItemClassName ? getItemClassName(item, index) : itemClassName}`} data-id="element-42" data-mp="4q4vvmhldgis" data-mpi={index}>
            {renderItem(item, index)}
          </li>)}
      </ul>

      <div className="mt-4 flex items-center justify-between gap-4" data-id="element-43" data-mp="3aoph2exblw5e">
        {mode === 'dots' ? <div className="flex flex-wrap items-center" data-id="element-44" data-mp="35oba707ggs8y">
            {items.map((item, index) => <button key={getKey(item, index)} type="button" onClick={() => goTo(index)} aria-label={`Go to item ${index + 1}`} aria-current={index === active} className="flex h-11 items-center px-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-ember/40" data-id="element-45" data-mp="30rb4iqeiprid" data-mpi={index}>
                <span className={`block h-[3px] rounded-full transition-[width,background-color] duration-200 ease-out ${index === active ? 'w-6 bg-ember' : `w-3 ${dark ? 'bg-ivory/25' : 'bg-ink/20'}`}`} data-id="element-46" data-mp="3jom0a10xy2t2" />
              </button>)}
          </div> : <div className="flex min-w-0 flex-1 items-center gap-3" aria-live="polite" data-id="element-47" data-mp="ji84hxy9m5du">
            <span className={`text-[13px] font-semibold tabular-nums ${dark ? 'text-ivory' : 'text-ink'}`} data-id="element-48" data-mp="njjwub8ix4pq">
              {pad(active + 1)}
              <span className={dark ? 'text-ivory/45' : 'text-ink/40'} data-id="element-49" data-mp="shbmeylp4eqr"> / {pad(items.length)}</span>
            </span>
            <span className={`relative h-[3px] max-w-[180px] flex-1 overflow-hidden rounded-full ${dark ? 'bg-ivory/15' : 'bg-ink/15'}`} aria-hidden data-id="element-50" data-mp="xfd5vkn1ag2r">
              <span className="absolute inset-y-0 left-0 rounded-full bg-ember transition-[width] duration-200 ease-out" style={{
            width: `${(active + 1) / items.length * 100}%`
          }} data-id="element-51" data-mp="1b9eagv0o2xb1" />
            </span>
          </div>}
        <div className="flex shrink-0 gap-2" data-id="element-52" data-mp="1l57ko7qwyepn">
          <button type="button" onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Previous" className={arrowClass} data-id="element-53" data-mp="1q5ltvt493up9">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden data-id="element-54" data-mp="vk7qceea9g2b" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} disabled={active === items.length - 1} aria-label="Next" className={arrowClass} data-id="element-55" data-mp="1a8ks5s01xt4q">
            <ChevronRightIcon className="h-5 w-5" aria-hidden data-id="element-56" data-mp="1j7pqax7pbkgd" />
          </button>
        </div>
      </div>
    </div>;
}